import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import 'rxjs/add/operator/toPromise';
import 'rxjs/add/observable/of';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/Observable';
import { catchError, tap } from 'rxjs/operators';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { Campaign } from './campaign.model';
import { CognitoService } from '../../services/cognito/cognito.service';
import { AwsSignerService } from '../../services/aws-signer/aws-signer.service';
import { Encoder } from '../../utils/encoder';
import { escape } from 'lodash';
import { CampaignListResponse } from './campaign-list-response.model';
import { GENDERS } from '../../services/constants/gender-list';
import { TransactionListResponse } from './transaction-list-response.model';

@Injectable()
export class CampaignService {
  hasJustRegistered: boolean;
  hasJustUpdated: boolean;
  hasJustDeleted: boolean;

  constructor(private http: HttpClient,
    private cognitoService: CognitoService,
    private awsSignerService: AwsSignerService) {
    this.hasJustRegistered = false;
    this.hasJustUpdated = false;
    this.hasJustDeleted = false;
  }

  getCampaignList(nextPageKey: string): Observable<any> {
    let url = this.getUrl();
    if (nextPageKey) {
      const encodedNextPageKey = Encoder.encode(nextPageKey);
      url = url.concat('?nextPageKey=' + encodedNextPageKey);
    }
    const awsHeaders = this.awsSignerService.getAwsHeader(url, 'GET');

    const headerParams = {
      'Content-Type': 'application/json',
      'Authorization': awsHeaders['Authorization'],
      'X-Amz-Date': awsHeaders['X-Amz-Date'],
      'x-amz-security-token': awsHeaders['x-amz-security-token'],
      'x-api-key': environment.cesAPI.apiKey,
    };

    if (this.hasJustRegistered) {
      // headerParams['Cache-Control'] = 'max-age=0';
    }

    const httpOptions = {
      headers: new HttpHeaders(headerParams)
    };

    return this.http.get<CampaignListResponse>(url, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }

  getSegmentList(nextPageKey?) {
    let url = `${this.getUrl()}/segment`;
    if (nextPageKey) {
      const encodedNextPageKey = Encoder.encode(nextPageKey);
      url = url.concat('?nextPageKey=' + encodedNextPageKey);
    }
    const awsHeaders = this.awsSignerService.getAwsHeader(url, 'GET');

    const headerParams = {
      'Content-Type': 'application/json',
      'Authorization': awsHeaders['Authorization'],
      'X-Amz-Date': awsHeaders['X-Amz-Date'],
      'x-amz-security-token': awsHeaders['x-amz-security-token'],
      'x-api-key': environment.cesAPI.apiKey,
    };

    const httpOptions = {
      headers: new HttpHeaders(headerParams)
    };

    return this.http.get(url, httpOptions)
  }

  saveCampaign(campaign: Campaign): Observable<any> {
    // const jwtToken = this.cognitoService.getJwtToken();
    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     'Content-Type': 'application/json',
    //     'Authorization': jwtToken,
    //     'x-api-key': environment.cesAPI.apiKey
    //   })
    // };
    campaign = this.cleanPostBody(campaign);
    const awsHeaders = this.awsSignerService.getAwsHeader(this.getUrl(), 'POST', campaign);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };

    return this.http.post<Campaign>(this.getUrl(), campaign, httpOptions)
      .pipe(
        tap(() => this.hasJustRegistered = true),
        catchError(err => this.handleError(err))
      );
  }

  getCampaignById(campaignId): Observable<any> {
    const encodedId = Encoder.encode(campaignId);
    const awsHeaders = this.awsSignerService.getAwsHeader(`${this.getUrl()}/${encodedId}`, 'GET');

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };

    return this.http.get<Campaign>(`${this.getUrl()}/${encodedId}`, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }

  getTimeZoneList(): Observable<any> {
    return this.http.get<Array<any>>(this.getConfigUrl() + "/time-zone.json")
      .pipe(catchError(err => this.handleError(err)));
  }

  getCurrencyCodeList(): Observable<any> {
    return this.http.get<Array<any>>(this.getConfigUrl() + "/currency-code.json")
      .pipe(catchError(err => this.handleError(err)));
  }

  getBrandList() {
    return this.http.get('assets/brand.json');
  }

  updateCampaign(campaign: Campaign): Observable<any> {
    campaign.campaignId = Encoder.escape(campaign.campaignId);

    const urlUpdate = `${this.getUrl()}/${Encoder.encode(campaign.campaignId)}`;
    // const jwtToken = this.cognitoService.getJwtToken();
    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     'Content-Type': 'application/json',
    //     'Authorization': jwtToken,
    //     'x-api-key': environment.cesAPI.apiKey
    //   })
    // };
    campaign = this.cleanPostBody(campaign);
    const awsHeaders = this.awsSignerService.getAwsHeader(urlUpdate, 'PUT', campaign);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };

    return this.http.put<Campaign>(urlUpdate, campaign, httpOptions)
      .pipe(
        tap(() => this.hasJustUpdated = true),
        catchError(err => this.handleError(err))
      );
  }

  getGenderList(): string[] {
    return GENDERS;
  }

  deleteCampaignById(campaignId): Observable<any> {
    const encodedId = Encoder.encode(campaignId);
    const awsHeaders = this.awsSignerService.getAwsHeader(`${this.getUrl()}/${encodedId}`, 'DELETE');

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };

    return this.http.delete<Campaign>(`${this.getUrl()}/${encodedId}`, httpOptions)
      .pipe(
        tap(() => this.hasJustDeleted = true),
        catchError(err => this.handleError(err))
      );
  }


  private cleanPostBody(campaign: Object) {
    for (var propName in campaign) {
      if (campaign[propName] === null || campaign[propName] === undefined) {
        delete campaign[propName];
      }
    }
    return campaign;
  }

  private getUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/manage/campaigns`;
  }

  private getClientUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/manage/client`;
  }

  private getConfigUrl() {
    return environment.configBasePath;
  }
  private getTransactionUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/transaction/campaigns`;
  }
  private getTransactionListUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/transaction/list`; 
  }
  private exportTransactionUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/transaction/campaignsEventTypeExport`;
  }

  getRegionList(): Observable<any> {
    return this.http.get<Array<any>>(this.getConfigUrl() + "/management-region.json")
      .pipe(catchError(err => this.handleError(err)));
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error && error.error.message === 'The security token included in the request is expired') {
      this.cognitoService.logout();
      return;
    }
    if (error.error instanceof ErrorEvent) {
      // A campaign-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an ErrorObservable with a user-facing error message
    return new ErrorObservable(
      'Something bad happened. Please try again later.');
  }
  getTransactionsByCampaign(campaignId: string, nextPageKey: string): Observable<any> {
    const encodedId = Encoder.encode(campaignId);
    let url = `${this.getTransactionUrl()}/${encodedId}`;
    if (nextPageKey) {
      const encodedNextPageKey = Encoder.encode(nextPageKey);
      url = url.concat('?nextPageKey=' + encodedNextPageKey);
    }

    const awsHeaders = this.awsSignerService.getAwsHeader(url, 'GET');

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };
    return this.http.get<TransactionListResponse>(url, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }

  getTransactionsList(clientId: string, apiKey: string, pageSize: string, lastEvaluatedKey: Object): Observable<any> {
    const body = {
      "clientId": clientId,
      "apiKey": apiKey,
      "pageSize": pageSize,
      "lastEvaluatedKey": lastEvaluatedKey
    }

   const awsHeaders = this.awsSignerService.getAwsHeader(this.getTransactionListUrl(), 'POST', body);
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };
    return this.http.post<TransactionListResponse>(this.getTransactionListUrl(), body, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }

  exportTransactionsByCampaign(campaignId: string, nextPageKey: string): Observable<any> {
    const encodedId = Encoder.encode(campaignId);
    let url = `${this.exportTransactionUrl()}/${encodedId}`;
    if (nextPageKey) {
      const encodedNextPageKey = Encoder.encode(nextPageKey);
      url = url.concat('?nextPageKey=' + encodedNextPageKey);
    }

    const awsHeaders = this.awsSignerService.getAwsHeader(url, 'GET');

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };
    return this.http.get<TransactionListResponse>(url, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }
  //save data to json file
  saveJSON(data, filename) {
    if (!data) {
      alert('data is null');
      return;
    }
    if (!filename) {
      filename = 'json.json'
    }
    if (typeof data === 'object') {
      data = JSON.stringify(data, undefined, 4)
    }
    // save file
    let blob = new Blob([data], { type: 'text/json' });
    let e = document.createEvent('MouseEvents');
    let a = document.createElement('a');
    a.download = filename;
    a.href = window.URL.createObjectURL(blob);
    a.dataset.downloadurl = ['text/json', a.download, a.href].join(':');
    e.initMouseEvent('click', true, false, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
    a.dispatchEvent(e);
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/campaign/campaign.service.ts